<template>
  <div style="position: relative" v-on:keydown.stop>
    <div class="backlog-container" style="position: static">
      <div class="backlog-actions-container">
        <add-card-button
          v-if="!showDone && !hasReleases && $route.params.eventId && defaultCardType?.title"
          :disabled="$store.state.isWorkflowDisabled"
          @click="addCardAtTop"
          class="mr-12"
        >
          <template #label>
            Add <b>{{ defaultCardType?.title }}</b>
          </template>
        </add-card-button>
        <v-switch
          :model-value="showDone"
          @update:model-value="$emit('display-done', $event)"
          label="Show Done"
          hide-details
          class="mt-0 pt-0 mr-12 flex-grow-0 flex-shrink-1"
        >
          <template #label="{ label }">
            <div class="text-no-wrap switcher-label"> {{ label }} </div>
          </template>
        </v-switch>
        <v-text-field
          variant="outlined"
          density="compact"
          class="backlog-search-field"
          v-model="search"
          append-inner-icon="mdi-magnify"
          label="Search"
          :single-line="true"
          hide-details
        ></v-text-field>
      </div>

      <cards-table
        v-if="Number(selectedTab) === 4"
        ref="cardsTable"
        :items="filteredItems ? filteredItems.cards : []"
        :events="filteredItems ? filteredItems.events : {}"
        :search="search"
        :idIndexMap="cardsIdIndexMap"
        :cardTypes="backlogCardTypes"
        :workflow="workflow"
        :jiraData2="jiraData2"
        :loadingJiraInfo="loadingJiraInfo"
        :showDone="showDone"
        @item-actions-done="doneCard"
        @assign-card="onAssignCard"
        @ask-for-reply="$emit('ask-for-reply', $event)"
        @drag-reorder="dragReorder"
      >
      </cards-table>
      <user-story-mapping
        v-if="Number(selectedTab) === 5"
        ref="userStoryMapping"
        :items="filteredItems ? filteredItems.cards : []"
        :events="filteredItems ? filteredItems.events : {}"
        :search="search"
        :idIndexMap="cardsIdIndexMap"
        :cardTypes="backlogCardTypes"
        :workflow="workflow"
        :showDone="showDone"
        :releases="releases"
        @item-actions-done="doneCard"
        @assign-card="onAssignCard"
      >
      </user-story-mapping>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import CardsTable from '@/components/CardsTable'
import AddCardButton from '@/components/AddCardButton'
import UserStoryMapping from '@/components/UserStoryMapping'
import midString from '@/utils/mid-string'
import workflowApi from '@/api/workflowApi'
import { cardHelpers } from '@/mixins/cardHelpers.js'
import cardHelpers2 from '@/helpers/cardHelpers.js'
import { DomainModelRole } from '@/constants/cardTypes'

export default {
  components: {
    CardsTable,
    UserStoryMapping,
    AddCardButton
  },
  mixins: [cardHelpers],
  props: [
    'eventId',
    'workflow',
    'showDone',
    'jiraData2',
    'loadingJiraInfo',
    'selectedTab',
    'releases'
  ],
  data () {
    return {
      valid: true,
      rules: {
        required: value => !!value || 'Required.',
        numbers: value => {
          const pattern = /^[0-9]*$/
          return pattern.test(value) || 'Only numbers.'
        },
        max100chars: (v) => (v && v.length <= 100) || 'Max 100 characters',
        max280chars: (v) => (v && v.length <= 280) || 'Max 280 characters',
        max500chars: (v) => (!v || (v && v.length <= 500)) || 'Max 500 characters'
      },
      activeTab: 0,
      search: null,
      selectedTypes: [],
      attachmentsDialog: false
    }
  },
  computed: {
    ...mapState({
      events: state => state.workflow.workflow.domainEvents,
      cardTypes: state => state.cardTypes.cardTypes,
      showReleaseNo: state => state.workflow.showReleaseNo,
      showTagCardType: state => state.workflow.showTagCardType
    }),
    ...mapGetters({
      defaultCardType: 'cardTypes/defaultCardType'
    }),
    backlogCardTypes () {
      return this.cardTypes?.filter(({ showInBacklog }) => showInBacklog)
    },
    filteredItems () {
      if (!this.events) return

      let cards = []
      const events = {}
      const displayTypeInBacklog = this.cardTypes.reduce((accum, { id, showInBacklog }) => {
        accum[id] = showInBacklog
        return accum
      }, {})

      for (const event of this.events) {
        if (event.cards) {
          cards = cards.concat(event.cards.map((card) => {
            return { ...card, eventId: event.id, releaseIndex: card.releaseNo || 0 }
          }))
        }
        events[event.id] = { ...event }
      }
      cards = cards
        .filter(({ done }) => this.showDone === Boolean(done))
        .filter(({ cardType }) => {
          if (!cardType || !Object.prototype.hasOwnProperty.call(displayTypeInBacklog, cardType.id)) {
            return true
          }
          return displayTypeInBacklog[cardType.id]
        })

      if (this.showReleaseNo && this.showReleaseNo.length > 0) {
        cards = cards.filter(({ releaseNo }) => this.showReleaseNo.includes(releaseNo))
      }

      // Filter by tag card type
      const tagCardTypes = this.cardTypes?.filter((ct) => ct.domainModelRole === DomainModelRole.TAG) || []
      const tagCardTypeId = tagCardTypes[0]?.id || null

      if (this.showTagCardType && this.showTagCardType.length > 0 && tagCardTypeId) {
        cards = cards.filter((card) => {
          // Check if card's event matches tag filter
          const event = events[card.eventId]
          if (!event || !event.cards) return false

          return event.cards.some((eventCard) => {
            if (eventCard.cardTypeId === tagCardTypeId) {
              const cardTags = this.getTagsForCard(eventCard)
              return cardTags.some((tag) => this.showTagCardType.includes(tag))
            }
            return false
          })
        })
      }

      cards = this.sortCards(cards)
      return { cards, events }
    },

    cardsIdIndexMap () {
      let cards = []

      if (!this.events) {
        return {}
      }

      for (const event of this.events) {
        if (event.cards) {
          cards = cards.concat(event.cards)
        }
      }

      const displayTypeInBacklog = {}

      if (Array.isArray(this.cardTypes)) {
        for (const { id, showInBacklog } of this.cardTypes) {
          displayTypeInBacklog[id] = showInBacklog
        }
      }

      cards = cards
        .filter(({ done }) => this.showDone === Boolean(done))
        .filter(({ cardType }) => {
          if (!cardType || !Object.prototype.hasOwnProperty.call(displayTypeInBacklog, cardType.id)) {
            return true
          }
          return displayTypeInBacklog[cardType.id]
        })
      cards = this.sortCards(cards)

      return cards.reduce((accumulator, currentValue, index) => {
        accumulator[currentValue.id] = index
        return accumulator
      }, {})
    },
    hasReleases () {
      if (!this.filteredItems || !this.filteredItems.cards) {
        return false
      }
      return this.filteredItems.cards.some(card => card.releaseNo > 0)
    }
  },
  methods: {
    getTagsForCard (card) {
      // Get tags directly from the card object (stored in backend)
      return card.tags || []
    },
    sortCards (cards) {
      cards.sort((a, b) => {
        const sortA = a.sortkey || 'zzzzzzzzzz'
        const sortB = b.sortkey || 'zzzzzzzzzz'
        const releaseA = a.releaseNo || '9999999999'
        const releaseB = b.releaseNo || '9999999999'
        if (releaseA > releaseB) {
          return 1
        } else if (releaseB > releaseA) {
          return -1
        } else {
          if (sortA === sortB) {
            return a.id > b.id ? 1 : -1
          } else {
            return sortA > sortB ? 1 : -1
          }
        }
      })
      return cards
    },
    sortOnPrio (teamMembersReactions) {
      // var res = teamMembersReactions.sort((a, b) => {
      //   return a.prio - b.prio
      // })
      // console.log('returning res', res)
      return teamMembersReactions
    },
    showAll () {
      this.$emit('show-all')
    },
    dragReorder ({ item, oldIndex, newIndex }) {
      let previousSortkey, nextSortkey

      // set new release number for the item that was dragged
      // and pass the update note only on the first item (should be refactored to make more sense)
      const newReleaseNoForItem = parseInt(this.getReleaseNoFromTableRowElement(item))
      const index = this.filteredItems.cards.findIndex(element => element.id === item.id)
      const cardsToUpdate = [{
        cardId: item.id,
        releaseNo: newReleaseNoForItem || null,
        updateNote: {
          action: 'moved card',
          target: cardHelpers2.getCardTarget(this.filteredItems.cards[index])
        }
      }]

      // get the sortkey from the previous row
      const previousEventId = item.previousElementSibling?.getAttribute('data-workflow-event-id') || null
      if (previousEventId) {
        previousSortkey = item.previousElementSibling.getAttribute('data-sortkey')
      }

      // if the next item has the same sortkey as the previous item (can happen when rearraging cards for one event at a time),
      // then we need to assign a new sortkey to this item, also check the following items
      let nextItem = item.nextElementSibling
      while (nextItem?.getAttribute('data-sortkey') === previousSortkey) {
        cardsToUpdate.push({
          cardId: nextItem.id,
          releaseNo: undefined // set it to undefined to avoid updating releaseNo (because null will update)
        })
        nextItem = nextItem.nextElementSibling
      }
      const nextEventId = nextItem?.getAttribute('data-workflow-event-id') || null
      if (nextEventId) {
        nextSortkey = nextItem.getAttribute('data-sortkey')
      }
      let card
      const cards = []
      // assign a new sortkey to the dragged item
      // and to all the following items, if there are items with duplicate sortkeys
      while ((card = cardsToUpdate.pop())) {
        const sortkey = this.midString(previousSortkey || '', nextSortkey || '')
        cards.push({ sortkey, ...card })
        nextSortkey = sortkey
      }
      for (let i = 0; i < cards.length; i += 5) {
        const cardChunks = cards.slice(i, i + 5)
        workflowApi.updateCardsMutation(this.$route.params.id, cardChunks)
      }
    },

    getReleaseNoFromTableRowElement (element) {
      const previousElement = element.previousElementSibling
      const nextElement = element.nextElementSibling
      let releaseNo = previousElement?.getAttribute('data-release-no')
      if (releaseNo) {
        return releaseNo
      }
      releaseNo = nextElement?.getAttribute('data-release-no')
      if (releaseNo) {
        return releaseNo
      }
      // don't know if the following 4 rows are needed
      releaseNo = previousElement?.firstElementChild?.getAttribute('data-release-no')
      if (releaseNo) {
        return releaseNo
      }
      return null
    },

    midString,

    doneCard (item, done) {
      let action
      if (done) {
        action = 'marked card as done'
      } else {
        action = 'reopened card'
      }
      workflowApi.updateCardsMutation(this.$route.params.id, [{
        cardId: item.id,
        done,
        updateNote: {
          action,
          target: cardHelpers2.getCardTarget(item)
        }
      }])
    },
    onAssignCard (item, userId) {
      // const { id: cardId, eventId } = item

      // return this.$apollo.mutate({
      //   mutation: AssignCard,
      //   variables: {
      //     id: cardId,
      //     projectId: this.$route.params.projectId,
      //     workflowId: this.$route.params.id,
      //     eventId,
      //     userId
      //   }
      // })
      //   .then(() => {
      //     this.$snackbar.showMessage({ content: 'Updated successfully' })
      //   })
      //   .catch(() => {
      //     this.error = 'An error occurred while updating card'
      //   })
    },
    async addCardAtTop () {
      try {
        const firstCard = this.filteredItems?.cards?.[0]
        const sortkey = cardHelpers2.generateNewSortKey('', firstCard?.sortkey || '')

        await workflowApi.createCard(
          this.$route.params.id,
          this.$route.params.eventId,
          {
            sortkey
          }
        )
      } catch (error) {
        this.$store.commit('snackbar/showMessage', {
          content: 'An error occurred while creating the card',
          color: 'red'
        })
      }
    }
  }
}
</script>

<style scoped>
.card-text {
  word-wrap: break-word; /* Wrap long words to fit within the width */
  white-space: normal; /* Allow text to wrap to multiple lines */
}

.backlog-actions-container {
  height: 36px;
  width: 50%;
  position: absolute;
  right: 0;
  top: -40px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.v-btn:not(.v-btn--depressed):not(.v-btn--flat) {
  box-shadow: none;
}

div.show-prio {
  text-transform: none;
}

.v-expansion-panel__header__icon {
  display: none;
}

.selected-type {
  margin-right: 4px;
}

.dragover-to-add-release {
  border: 2px dashed gray;
}

.switcher-label {
  margin-left: -4px;
}
</style>

<style>
.backlog-search-field {
  max-width: 220px !important;
}

.backlog-search-field .v-input__control {
  min-height: 36px !important;
}

.backlog-search-field .v-field {
  min-height: 36px !important;
  border-radius: 10px !important;
}

.backlog-search-field .v-input__slot .v-label {
  font-size: 14px;
  top: 8px !important;
}

.backlog-search-field .v-input__append-inner {
  margin-top: 6px !important;
}
</style>
